import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { LogOut, Menu, PanelLeftClose, PanelLeftOpen, Search } from 'lucide-react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { PlatformApiError, platformApiRequest } from '../api/client.ts';
import { BrandMark, BrandWordmark } from '../components/BrandWordmark.tsx';
import { useSession } from '../hooks/useSession.tsx';
import { LocaleSwitcher, useT } from '../i18n/index.ts';
import { Chip, Drawer, Icon, IconButton, StatusLamp, Tooltip, cx } from '../ui/index.ts';
import { ShellCommandPalette } from './ShellCommandPalette.tsx';
import { titleItemForPath, visibleNavGroups } from './navigation.ts';
import type { NavGroup } from './navigation.ts';
import { pushRecentPath } from './recentPaths.ts';

/**
 * Dispatch Deck app chrome for the tenant console (epic #897 PR 2,
 * modeled on the ops-console Shell) — sidebar + header + main.
 *
 *   - Sidebar: 224px, collapsible to a 56px icon rail (persisted in
 *     `console.sidebar.rail`); below lg it becomes a Drawer behind the
 *     header hamburger (the old fixed 256px column had zero responsive
 *     handling).
 *   - Header: hamburger (mobile), page title, ⌘K command palette
 *     trigger styled as a search field, locale switcher.
 *   - Footer identity card: account email + verified chip + logout +
 *     rail toggle. Tenants have permissions, not roles — nav filtering
 *     runs through `hasPermission` in `navigation.ts`.
 *   - Every visited nav destination is recorded for the palette's
 *     "recent" group.
 *
 * `data-testid` values (`shell-sidebar`, `shell-nav-*`,
 * `shell-page-title`, `shell-email`, `shell-verified`, `shell-logout`)
 * are unchanged so existing test selectors keep working.
 */
interface Props {
  readonly children?: ReactNode;
}

const RAIL_STORAGE_KEY = 'console.sidebar.rail';

function loadRailPreference(): boolean {
  try {
    return window.localStorage.getItem(RAIL_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function Shell({ children }: Props): JSX.Element {
  const { user, clearUser, fetchImpl } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const t = useT();
  const navGroups = useMemo(() => visibleNavGroups(user), [user]);

  const [rail, setRail] = useState(loadRailPreference);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Single source for "where am I": the longest permission-visible nav
  // match (targets + legacy aliases). Nav highlighting derives from
  // this so nested routes (/billing/topup, /team/invitations) light
  // exactly ONE item.
  const activeNavItem = useMemo(
    () => titleItemForPath(user, location.pathname),
    [location.pathname, user],
  );
  const activeNavPath = activeNavItem?.to ?? null;
  const pageTitle =
    activeNavItem !== null ? t(activeNavItem.labelKey) : t('shell.page.console_fallback');

  // Record nav destinations for the palette's "recent" group and close
  // the mobile drawer after any navigation.
  useEffect(() => {
    if (activeNavPath !== null) {
      pushRecentPath(activeNavPath);
    }
    setMobileNavOpen(false);
  }, [activeNavPath, location.pathname]);

  // Global ⌘K / Ctrl+K.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPaletteOpen((prev) => !prev);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  function toggleRail(): void {
    setRail((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(RAIL_STORAGE_KEY, next ? '1' : '0');
      } catch {
        // Preference only.
      }
      return next;
    });
  }

  async function handleLogout(): Promise<void> {
    try {
      await platformApiRequest<{ ok: boolean }>('/v1/auth/logout', { method: 'POST' }, fetchImpl);
    } catch (err) {
      // Even if /logout fails we clear the client-side mirror so the UI
      // doesn't stay in an inconsistent "logged in" state. The cookie
      // will expire on its own TTL regardless.
      if (!(err instanceof PlatformApiError)) {
        throw err;
      }
    }
    clearUser();
    navigate('/login', { replace: true });
  }

  const displayEmail =
    user?.email !== undefined && user.email.length > 0 ? user.email : t('shell.session.signed_in');
  const verified = user?.emailVerified === true;

  return (
    <div className="flex h-screen overflow-hidden bg-canvas text-paper">
      {/* Desktop sidebar */}
      <aside
        data-testid="shell-sidebar"
        className={cx(
          'hidden min-h-0 shrink-0 flex-col border-r border-line-soft bg-well transition-[width] duration-200 lg:flex',
          rail ? 'w-14' : 'w-56',
        )}
      >
        <div className={cx('flex shrink-0 items-center px-3 pb-4 pt-5', rail && 'justify-center px-0')}>
          {rail ? (
            <BrandMark className="h-8 w-8 shrink-0" />
          ) : (
            <BrandWordmark descriptor={t('shell.brand.subtitle')} testId="shell-brand-wordmark" />
          )}
        </div>
        <nav
          aria-label={t('shell.nav.aria_label')}
          className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain px-2 pb-2"
        >
          <NavList groups={navGroups} rail={rail} activePath={activeNavPath} t={t} />
        </nav>
        <div className={cx('mt-auto flex shrink-0 flex-col gap-2 border-t border-line-soft p-2', rail && 'items-center')}>
          {!rail ? (
            <div className="flex flex-col gap-1.5 rounded-lg border border-line-soft bg-card px-2.5 py-2">
              <span className="truncate text-label text-paper" data-testid="shell-email">
                {displayEmail}
              </span>
              <Chip tone={verified ? 'ok' : 'warn'} lamp={false} className="w-fit" testId="shell-verified">
                {verified ? t('shell.session.verified') : t('shell.session.unverified')}
              </Chip>
            </div>
          ) : (
            <span className="sr-only" data-testid="shell-email">
              {displayEmail}
            </span>
          )}
          <div className={cx('flex items-center gap-1', rail && 'flex-col')}>
            <IconButton
              size="sm"
              label={t('shell.logout')}
              onClick={() => {
                void handleLogout();
              }}
              data-testid="shell-logout"
            >
              <LogOut aria-hidden="true" className="h-4 w-4" />
            </IconButton>
            <IconButton
              size="sm"
              label={rail ? t('shell.sidebar.expand') : t('shell.sidebar.collapse')}
              onClick={toggleRail}
              data-testid="shell-rail-toggle"
              className={cx(!rail && 'ml-auto')}
            >
              {rail ? (
                <PanelLeftOpen aria-hidden="true" className="h-4 w-4" />
              ) : (
                <PanelLeftClose aria-hidden="true" className="h-4 w-4" />
              )}
            </IconButton>
          </div>
        </div>
      </aside>

      {/* Mobile nav drawer */}
      <Drawer
        open={mobileNavOpen}
        onOpenChange={setMobileNavOpen}
        title={t('shell.brand.title')}
        width="sm"
        closeLabel={t('shell.nav.close')}
        testId="shell-mobile-nav"
      >
        <nav aria-label={t('shell.nav.aria_label')} className="-mx-2 flex flex-col">
          <NavList groups={navGroups} rail={false} activePath={activeNavPath} t={t} />
        </nav>
        <div className="mt-4 flex items-center justify-between gap-2 border-t border-line-soft pt-3">
          <span className="truncate text-label text-paper-mid">{displayEmail}</span>
          <IconButton
            size="sm"
            label={t('shell.logout')}
            onClick={() => {
              void handleLogout();
            }}
          >
            <LogOut aria-hidden="true" className="h-4 w-4" />
          </IconButton>
        </div>
      </Drawer>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-10 flex h-14 shrink-0 items-center gap-3 border-b border-line-soft bg-canvas px-6 max-lg:px-4">
          <IconButton
            label={t('shell.nav.open')}
            onClick={() => setMobileNavOpen(true)}
            className="lg:hidden"
            data-testid="shell-mobile-nav-open"
          >
            <Menu aria-hidden="true" className="h-4 w-4" />
          </IconButton>
          <span className="truncate text-section text-paper" data-testid="shell-page-title">
            {pageTitle}
          </span>
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            data-testid="shell-cmdk-trigger"
            className="ml-2 hidden h-8 min-w-0 max-w-md flex-1 items-center gap-2 rounded border border-line bg-well px-3 text-dense text-paper-low transition-colors duration-120 hover:border-line-strong hover:text-paper-mid focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40 md:flex"
          >
            <Search aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{t('shell.cmdk.trigger')}</span>
            <kbd className="ml-auto shrink-0 rounded-sm border border-line px-1.5 font-mono text-micro text-paper-low">
              ⌘K
            </kbd>
          </button>
          <IconButton label={t('shell.cmdk.trigger')} onClick={() => setPaletteOpen(true)} className="md:hidden">
            <Search aria-hidden="true" className="h-4 w-4" />
          </IconButton>
          <div className="ml-auto flex items-center gap-3">
            <LocaleSwitcher />
          </div>
        </header>
        <main className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain p-6 max-lg:p-4">
          {children ?? <Outlet />}
        </main>
      </div>

      <ShellCommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  );
}

interface NavListProps {
  readonly groups: readonly NavGroup[];
  readonly rail: boolean;
  readonly activePath: string | null;
  readonly t: ReturnType<typeof useT>;
}

function NavList({ groups, rail, activePath, t }: NavListProps): JSX.Element {
  return (
    <>
      {groups.map((group) => (
        <div key={group.titleKey} className="flex flex-col gap-0.5">
          {!rail ? (
            <span className="px-2.5 pb-1 pt-4 text-micro text-paper-low">{t(group.titleKey)}</span>
          ) : (
            <span aria-hidden="true" className="mx-3 mb-1 mt-4 h-px bg-line-soft" />
          )}
          {group.items.map((item) => {
            // Longest-match active state (computed once in Shell) so
            // exactly one item lights up on nested routes.
            const isActive = item.to === activePath;
            const link = (
              <Link
                key={item.to}
                to={item.to}
                aria-current={isActive ? 'page' : undefined}
                data-testid={`shell-nav-${item.testId}`}
                className={cx(
                  'flex items-center gap-2.5 rounded px-2.5 py-1.5 text-dense font-medium transition-colors duration-120 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40',
                  rail && 'justify-center px-0 py-2',
                  isActive
                    ? 'bg-signal-dim text-signal'
                    : 'text-paper-mid hover:bg-card hover:text-paper',
                )}
              >
                <Icon name={item.icon} size={rail ? 'md' : 'sm'} className="text-current" />
                {!rail ? (
                  <>
                    <span className="min-w-0 flex-1 truncate">{t(item.labelKey)}</span>
                    {isActive ? <StatusLamp tone="signal" size="sm" /> : null}
                  </>
                ) : null}
              </Link>
            );
            return rail ? (
              <Tooltip key={item.to} content={t(item.labelKey)} side="right">
                {link}
              </Tooltip>
            ) : (
              link
            );
          })}
        </div>
      ))}
    </>
  );
}
